import type { Area } from 'react-easy-crop';
import { supabase } from './supabase';

const VENUE_PHOTOS_BUCKET = 'venue-photos';

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not load image for cropping'));
    img.src = src;
  });
}

/** Crop a region from an image URL into a JPEG blob. */
export async function cropImageToJpegBlob(imageSrc: string, pixelCrop: Area, quality = 0.92): Promise<Blob> {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported');

  canvas.width = Math.max(1, Math.round(pixelCrop.width));
  canvas.height = Math.max(1, Math.round(pixelCrop.height));

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) reject(new Error('Could not export cropped image'));
        else resolve(blob);
      },
      'image/jpeg',
      quality,
    );
  });
}

/** Extract storage object path from a venue-photos public URL, if applicable. */
export function venuePhotoStoragePath(publicUrl: string): string | null {
  const marker = `/${VENUE_PHOTOS_BUCKET}/`;
  const idx = publicUrl.indexOf(marker);
  if (idx < 0) return null;
  const path = publicUrl.slice(idx + marker.length).split('?')[0];
  return path || null;
}

export async function uploadVenuePhotoBlob(
  path: string,
  blob: Blob,
): Promise<{ publicUrl: string; storagePath: string }> {
  const { error } = await supabase.storage.from(VENUE_PHOTOS_BUCKET).upload(path, blob, {
    contentType: 'image/jpeg',
    upsert: true,
  });
  if (error) throw error;
  const { data } = supabase.storage.from(VENUE_PHOTOS_BUCKET).getPublicUrl(path);
  if (!data.publicUrl) throw new Error('Could not resolve public URL after upload');
  return { publicUrl: data.publicUrl, storagePath: path };
}

/**
 * Square-re-crop marker + path builder.
 *
 * The app renders venue heroes at 1:1 (`screens/venue-discovery.tsx` `aspectRatio: 1`), but every
 * photo approved before 2026-08 was composed in the 4:3 crop modal. The Photo Approval tab re-cuts
 * those from the user's ORIGINAL flash photo and always uploads to a NEW storage path, for two
 * reasons:
 *
 *  1. Cache-busting — overwriting the same object leaves the Supabase/CDN edge serving the stale
 *     image, so a re-crop silently looks like it did nothing.
 *  2. The `-sq-<timestamp>` suffix marks a file as square-composed, with NO schema change.
 *
 * `-sq.jpg` (no timestamp) is also matched so any hand-made square file is recognised.
 *
 * NOTE (2026-08-02): {@link isSquareRecropUrl} is no longer how the UI decides what is outstanding
 * — Photo Approval classifies each row by whether the rater's ORIGINAL can be resolved and whether
 * it is square (`src/lib/venuePhotoOriginals.ts`), which is a fact about the data rather than about
 * a filename. The marker is kept because the paths already exist in Storage.
 */
const SQUARE_RECROP_URL_RE = /-sq(-\d+)?\.jpe?g(\?.*)?$/i;

/** True when this approved-photo URL was produced by the square re-crop flow. */
export function isSquareRecropUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  return SQUARE_RECROP_URL_RE.test(url);
}

/** New, never-before-used object path for a square re-crop (fresh every call — see note above). */
export function buildSquareRecropPath(venueId: string, photoKey: string): string {
  return `admin-approved/${venueId}/${photoKey}-sq-${Date.now()}.jpg`;
}

/** Prefer signed URL for canvas cropping (CORS-safe when bucket is private). */
export async function resolveVenuePhotoCropUrl(publicUrl: string): Promise<string> {
  const path = venuePhotoStoragePath(publicUrl);
  if (path) {
    const { data, error } = await supabase.storage.from(VENUE_PHOTOS_BUCKET).createSignedUrl(path, 3600);
    if (!error && data?.signedUrl) return data.signedUrl;
  }
  return publicUrl;
}
